import moment from "moment-timezone";
import i18n from "i18next";
import { getRegistrationData } from "../registrationSetupStorage";

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #5a6b80, #2d3f52)",
  "linear-gradient(135deg, #209092, #166a6c)",
  "linear-gradient(135deg, #e48ba4, #c56a84)",
  "linear-gradient(135deg, #aece38, #8aab15)",
  "linear-gradient(135deg, #6b7fd7, #3d4e9e)",
];

const LANGUAGE_ID_BY_CODE = {
  en: "1",
  "en-US": "1",
  es: "2",
};

/** Decode literal \\n / \\t style sequences from API profile copy. */
export const formatRdProfileText = (value) => {
  if (value == null || value === "") {
    return "";
  }

  let text = String(value);

  text = text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\\t/g, "\t");

  return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
};

export const NO_PREFERENCE_RD_OPTION = {
  id: "no-preference",
  isNoPreference: true,
  initials: "📅",
  avatarStyle: "linear-gradient(135deg, #aece38, #8aab15)",
  name: "",
  creds: "",
  description: "",
  nextAvailable: "",
  tags: [],
  states: "",
  about: "",
  raw: null,
};

export const getRdProfileImage = (rd) => {
  const candidates = [
    rd?.profile_image,
    rd?.profileImage,
    rd?.rd_detail?.profile_image,
    rd?.rd_detail?.profileImage,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== "string") {
      continue;
    }

    const trimmed = candidate.trim();
    if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
      return trimmed;
    }
  }

  return "";
};

export const hasRdProfileImage = (profileImage) =>
  typeof profileImage === "string" && profileImage.trim().length > 0;

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) {
    return "RD";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

export const getRegistrationLanguageId = (language = "en") => {
  const normalized = String(language || "en").trim();
  return LANGUAGE_ID_BY_CODE[normalized] ?? LANGUAGE_ID_BY_CODE.en;
};

export const buildListMatchedRdPayload = (data) => {
  const categoryId = data?.categoryId ?? data?.category_id;
  const explicitLanguageId = data?.languageId ?? data?.language_id;
  const payload = {
    category_id: categoryId != null ? String(categoryId) : "",
    // UI locale (en/es) is not an RD spoken-language filter; omit unless explicitly stored.
    language_id:
      explicitLanguageId != null && explicitLanguageId !== ""
        ? String(explicitLanguageId)
        : "",
  };

  const timezoneId = data?.timezoneId ?? data?.timezone_id;
  if (timezoneId != null && timezoneId !== "") {
    const parsedTimezoneId = Number(timezoneId);
    if (!Number.isNaN(parsedTimezoneId)) {
      payload.timezone_id = parsedTimezoneId;
    }
  }

  return payload;
};

const formatNextAvailable = (rd) => {
  const startTime =
    rd?.nextSlotTime?.start_time ??
    rd?.next_slot_time?.start_time ??
    rd?.next_available ??
    rd?.next_available_at;

  if (!startTime) {
    return "";
  }

  return moment.tz(startTime, moment.tz.guess()).format("ddd MMM D · h:mm A");
};

/** Earliest next-available slot across matched RDs (for no-preference card). */
export const getEarliestNextAvailable = (list) => {
  if (!Array.isArray(list) || list.length === 0) {
    return "";
  }

  const earliest = list.reduce((best, rd) => {
    const startTime =
      rd?.nextSlotTime?.start_time ??
      rd?.next_slot_time?.start_time ??
      rd?.next_available ??
      rd?.next_available_at;

    if (!startTime) {
      return best;
    }

    const candidate = moment.tz(startTime, moment.tz.guess());
    if (!candidate.isValid()) {
      return best;
    }

    return !best || candidate.isBefore(best) ? candidate : best;
  }, null);

  return earliest ? earliest.format("ddd MMM D · h:mm A") : "";
};

const mapSpecialtyTags = (rd) => {
  if (Array.isArray(rd?.categories) && rd.categories.length > 0) {
    return rd.categories
      .map((item) => item?.name)
      .filter((name) => Boolean(name));
  }
  if (Array.isArray(rd?.specialities)) {
    return rd.specialities.filter(Boolean);
  }
  if (Array.isArray(rd?.specialties)) {
    return rd.specialties.filter(Boolean);
  }
  const primary = rd?.rd_detail?.primary_category?.name;
  const secondary = rd?.rd_detail?.secondary_category?.name;
  return [primary, secondary].filter(Boolean);
};

const mapLicensedStates = (rd) => {
  const codes = mapLicensedStateCodes(rd);
  if (codes.length > 0) {
    return codes.join(" · ");
  }

  if (typeof rd?.licensed_states === "string") {
    return rd.licensed_states;
  }
  if (typeof rd?.state === "string") {
    return rd.state;
  }
  return "";
};

const extractStateLicenseCode = (item) => {
  if (typeof item === "string") {
    return item.trim();
  }
  if (item && typeof item === "object") {
    return String(item.code ?? item.state_code ?? "").trim();
  }
  return "";
};

export const mapLicensedStateCodes = (rd) => {
  const sources = [
    rd?.state_license_codes,
    rd?.rd_detail?.state_license_codes,
    rd?.licensed_states,
    rd?.rd_detail?.licensed_states,
    rd?.states,
    rd?.rd_detail?.states,
  ];

  for (const source of sources) {
    if (!Array.isArray(source) || source.length === 0) {
      continue;
    }

    const codes = source.map(extractStateLicenseCode).filter(Boolean);
    if (codes.length > 0) {
      return [...new Set(codes)];
    }
  }

  return [];
};


export const MODALITY_JOIN_SEPARATOR = " · ";

export const MODALITY_LABELS = {
  TELEHEALTH: "Telehealth",
  IN_PERSON: "In Person",
};

export const isInPersonModality = (label) => {
  const normalized = String(label ?? "").trim().toLowerCase();
  return normalized === "in person" || normalized === "in-person";
};

export const isTelehealthModality = (label) =>
  String(label ?? "").trim().toLowerCase() === "telehealth";

const isVirtualModalityValue = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized === "virtual" ||
    normalized === "google_conference" ||
    normalized.includes("video") ||
    normalized.includes("telehealth")
  );
};

/** Maps rd_calendly_urls[].type to a display label. */
export const mapRdTypeToModalityLabel = (type) => {
  const normalized = String(type ?? "").trim().toLowerCase();
  if (!normalized) {
    return "";
  }
  if (isVirtualModalityValue(normalized)) {
    return MODALITY_LABELS.TELEHEALTH;
  }
  if (
    normalized === "in-person" ||
    normalized === "in_person" ||
    normalized === "in person" ||
    normalized === "initial-in-person" ||
    normalized.includes("in-person") ||
    normalized.includes("in_person")
  ) {
    return MODALITY_LABELS.IN_PERSON;
  }
  return "";
};

const getRdCalendlyUrlEntries = (rd) => {
  if (Array.isArray(rd?.rd_calendly_urls) && rd.rd_calendly_urls.length > 0) {
    return rd.rd_calendly_urls;
  }
  if (Array.isArray(rd?.calendly_urls) && rd.calendly_urls.length > 0) {
    return rd.calendly_urls;
  }
  if (rd?.calendly_url && typeof rd.calendly_url === "object") {
    return [rd.calendly_url];
  }
  return [];
};

const resolveRdCalendlyUrlModalityLabel = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const typeCandidates = [item.type, item.visit_type, item.visit_type_label];
  for (const candidate of typeCandidates) {
    const fromType = mapRdTypeToModalityLabel(candidate);
    if (fromType) {
      return fromType;
    }
  }

  const fromLocation = mapSlotLocationToLabel(item.location);
  if (isTelehealthModality(fromLocation) || isInPersonModality(fromLocation)) {
    return fromLocation;
  }

  return "";
};

/** Maps slot location strings to Telehealth / In Person, or returns the value as-is. */
export const mapSlotLocationToLabel = (location) => {
  const raw = String(location ?? "").trim();
  if (!raw) {
    return "";
  }

  const normalized = raw.toLowerCase();
  if (isVirtualModalityValue(normalized)) {
    return MODALITY_LABELS.TELEHEALTH;
  }

  if (
    normalized.includes("in-person") ||
    normalized.includes("in_person") ||
    normalized.includes("in person")
  ) {
    return MODALITY_LABELS.IN_PERSON;
  }

  return raw;
};

/** @deprecated Use mapSlotLocationToLabel for slots or mapRdTypeToModalityLabel for RD types. */
export const mapLocationToModalityLabel = mapSlotLocationToLabel;

const joinModalityLabels = (labels) => {
  const seen = new Set();
  const ordered = [];

  labels.forEach((label) => {
    const trimmed = String(label ?? "").trim();
    if (!trimmed || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    ordered.push(trimmed);
  });

  return ordered.join(MODALITY_JOIN_SEPARATOR);
};

export const getRdModalityLabel = (rd) => {
  const urls = getRdCalendlyUrlEntries(rd);
  if (urls.length === 0) {
    return "";
  }

  const labels = urls
    .map((item) => resolveRdCalendlyUrlModalityLabel(item))
    .filter(Boolean);

  return joinModalityLabels(labels);
};

const buildRdCreds = (rd) => getRdModalityLabel(rd);

export const findMatchedRdById = (rdId) => {
  const data = getRegistrationData();
  const list = data?.matchedRdList;
  if (!Array.isArray(list)) {
    return null;
  }
  return (
    list.find(
      (item) =>
        String(item?.id ?? item?.rd_id ?? item?.user_id) === String(rdId)
    ) ?? null
  );
};

const enrichRdWithMatchedListData = (rd) => {
  if (!rd || typeof rd !== "object") {
    return rd;
  }

  const rdId = rd?.id ?? rd?.rd_id ?? rd?.user_id;
  const matched = findMatchedRdById(rdId);
  if (!matched) {
    return rd;
  }

  const updates = {};

  if (
    (!Array.isArray(rd.rd_calendly_urls) || rd.rd_calendly_urls.length === 0) &&
    matched
  ) {
    if (Array.isArray(matched.rd_calendly_urls) && matched.rd_calendly_urls.length > 0) {
      updates.rd_calendly_urls = matched.rd_calendly_urls;
    } else if (matched.calendly_url) {
      updates.rd_calendly_urls = [matched.calendly_url];
    }
  }

  if (
    (!updates.rd_calendly_urls || updates.rd_calendly_urls.length === 0) &&
    (!Array.isArray(rd.rd_calendly_urls) || rd.rd_calendly_urls.length === 0) &&
    rd?.calendly_url
  ) {
    updates.rd_calendly_urls = [rd.calendly_url];
  }

  if (
    (!Array.isArray(rd.categories) || rd.categories.length === 0) &&
    Array.isArray(matched.categories) &&
    matched.categories.length > 0
  ) {
    updates.categories = matched.categories;
  }

  return Object.keys(updates).length > 0 ? { ...rd, ...updates } : rd;
};

export const mapMatchedRdToSelectionCard = (rd, index) => {
  const enrichedRd = enrichRdWithMatchedListData(rd);
  const firstName = enrichedRd?.first_name ?? enrichedRd?.firstName ?? "";
  const lastName = enrichedRd?.last_name ?? enrichedRd?.lastName ?? "";
  const name =
    `${firstName} ${lastName}`.trim() ||
    enrichedRd?.name ||
    i18n.t("NUTRITION_COMMON_LBL_RD_FALLBACK");
  const id = String(enrichedRd?.id ?? enrichedRd?.rd_id ?? enrichedRd?.user_id ?? index);

  return {
    id,
    isNoPreference: false,
    initials: getInitials(name),
    avatarStyle: AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length],
    profileImage: getRdProfileImage(enrichedRd),
    name,
    creds: buildRdCreds(enrichedRd),
    tags: mapSpecialtyTags(enrichedRd),
    states: mapLicensedStates(enrichedRd),
    about: formatRdProfileText(
      enrichedRd?.rd_detail?.profile_text ??
        enrichedRd?.profile_text ??
        enrichedRd?.about ??
        ""
    ),
    nextAvailable: formatNextAvailable(enrichedRd),
    description: "",
    raw: enrichedRd,
  };
};

export const mapMatchedRdListToSelectionCards = (
  list,
  noPreferenceNextAvailable = "",
  t = null
) => {
  const resolvedNoPreferenceNextAvailable =
    noPreferenceNextAvailable || getEarliestNextAvailable(list);

  const noPreferenceCard = t
    ? {
        id: "no-preference",
        isNoPreference: true,
        initials: "📅",
        avatarStyle: "linear-gradient(135deg, #aece38, #8aab15)",
        name: t("NUTRITION_RDLIST_LBL_NO_PREF_TITLE"),
        creds: t("NUTRITION_RDLIST_LBL_NO_PREF_SUB"),
        description: t("NUTRITION_RDLIST_DESC_NO_PREF"),
        nextAvailable: resolvedNoPreferenceNextAvailable,
        tags: [],
        states: "",
        about: "",
        raw: null,
      }
    : {
        ...NO_PREFERENCE_RD_OPTION,
        nextAvailable: resolvedNoPreferenceNextAvailable,
      };

  if (!Array.isArray(list)) {
    return [noPreferenceCard];
  }

  const cards = list.map((rd, index) => mapMatchedRdToSelectionCard(rd, index));
  return [noPreferenceCard, ...cards];
};

export const mapMatchedRdToProfile = (rd) => {
  if (!rd || typeof rd !== "object") {
    return null;
  }

  const enrichedRd = enrichRdWithMatchedListData(rd);
  const firstName = enrichedRd?.first_name ?? enrichedRd?.firstName ?? "";
  const lastName = enrichedRd?.last_name ?? enrichedRd?.lastName ?? "";
  const name =
    `${firstName} ${lastName}`.trim() ||
    enrichedRd?.name ||
    i18n.t("NUTRITION_COMMON_LBL_RD_FALLBACK");
  const specialties = mapSpecialtyTags(enrichedRd);
  const licensedIn = mapLicensedStateCodes(enrichedRd);

  return {
    id: String(enrichedRd?.id ?? enrichedRd?.rd_id ?? enrichedRd?.user_id ?? ""),
    initials: getInitials(name),
    avatarStyle: AVATAR_GRADIENTS[0],
    profileImage: getRdProfileImage(enrichedRd),
    name,
    fullName: enrichedRd?.rd_detail?.full_name ?? name,
    creds: buildRdCreds(enrichedRd),
    experience: enrichedRd?.experience ?? enrichedRd?.rd_detail?.experience ?? "",
    about: formatRdProfileText(
      enrichedRd?.rd_detail?.profile_text ??
        enrichedRd?.profile_text ??
        enrichedRd?.about ??
        ""
    ),
    approach: formatRdProfileText(
      enrichedRd?.rd_detail?.approach ?? enrichedRd?.approach ?? ""
    ),
    specialties,
    licensedIn,
    raw: enrichedRd,
  };
};

export const mapRdProfileApiToView = (apiData) => {
  const rd = apiData?.rd_detail
    ? { ...apiData, rd_detail: apiData.rd_detail }
    : apiData?.data && typeof apiData.data === "object"
      ? apiData.data
      : apiData;

  return mapMatchedRdToProfile(rd);
};
